import { useState, useEffect } from "react";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import EDTApercuByTeacher from "./edt_apercu_modal_by_teacher";

type Timetable = {
  id: number;
  label: string; // Exemple : "Semaine 03 (01/09 - 08/09/2025)"
  start: string; // date au format "YYYY-MM-DD"
  end: string; // date au format "YYYY-MM-DD"
};

type Props = {
  timetables: any[];
  onChange?: (timetable: Timetable) => void; // nouveau props pour notifier le parent
};

const EDTTooltipOption = ({ timetables, onChange }: Props) => {
  //Initialisation des state utilisés
  const [selected, setSelected] = useState<any>(null);

  const [showTeacherModal, setShowTeacherModal] = useState(false);
  // Trouver l’emploi du temps courant à l’init
  useEffect(() => {
    if (!selected) {
      // uniquement si rien n'est encore sélectionné
      const today = new Date();

      // Essayer de trouvé l'emploisdu temps qui contient aujourd'hui
      let current = timetables.find((t) => {
        const start = new Date(t?.valid_from);
        const end = new Date(t?.valid_until);
        return today >= start && today <= end;
      });

      // Si aucun emploi ne contient aujourd’hui, on choisit celui avec la date de début la plus récente
      if (!current && timetables.length > 0) {
        current = timetables.reduce((latest, t) =>
          new Date(t.valid_from) > new Date(latest.valid_from) ? t : latest,
        );
      }

      if (current) {
        setSelected(current);
        if (onChange) onChange(current);
      } else if (timetables.length > 0) {
        setSelected(timetables[0]);
        if (onChange) onChange(timetables[0]);
      }
    }
  }, [timetables, onChange, selected]);

  const handleSelect = (t: Timetable) => {
    setSelected(t);
    if (onChange) onChange(t); // notifier le parent quand on change
  };

  return (
    <>
      <div className="pe-1 mb-2">
        <button
          type="button"
          className="btn btn-outline-light bg-white btn-icon me-1"
          title="Imprimer"
          onClick={() => setShowTeacherModal(true)}
        >
          <i className="ti ti-printer" />
        </button>
      </div>

      {/* Navigation arrows + dropdown for mobile, dropdown only for desktop */}
      <div className="d-flex align-items-center me-2 mb-2">
        {/* Previous arrow */}
        <button
          className="btn btn-light btn-icon me-1"
          onClick={() => {
            const idx = timetables.findIndex(
              (t) => t?.edt_id === selected?.edt_id,
            );
            if (idx > 0) handleSelect(timetables[idx - 1]);
          }}
          disabled={
            !selected ||
            timetables.findIndex((t) => t?.edt_id === selected?.edt_id) === 0
          }
          title="Emploi du temps précédent"
        >
          <i className="ti ti-chevron-left" />
        </button>

        {/* Dropdown */}
        <div className="dropdown">
          <button
            className="dropdown-toggle btn btn-light fw-medium d-inline-flex align-items-center text-truncate"
            data-bs-toggle="dropdown"
            style={{ maxWidth: "70vw" }}
          >
            <i className="ti ti-calendar-event me-2" />
            {selected
              ? `${selected.edt_title} (${new Date(
                  selected.valid_from,
                ).toLocaleDateString("fr-FR")} au ${new Date(
                  selected.valid_until,
                ).toLocaleDateString("fr-FR")})`
              : "Sélectionner un emploi du temps"}
          </button>

          <ul
            className="dropdown-menu dropdown-menu-end p-2"
            style={{
              maxHeight: "300px",
              overflow: "auto",
              maxWidth: "70vw",
              width: "max-content",
            }}
          >
            {timetables
              .slice()
              .sort(
                (a, b) =>
                  new Date(b.valid_from).getTime() -
                  new Date(a.valid_from).getTime(),
              )
              .map((t) => (
                <li key={t?.edt_id}>
                  <button
                    className={`dropdown-item rounded-1 ${
                      selected?.edt_id === t?.edt_id ? "active" : ""
                    }`}
                    onClick={() => handleSelect(t)}
                  >
                    <i className="ti ti-calendar-time me-1" />
                    {t?.edt_title} (
                    {new Date(t.valid_from).toLocaleDateString("fr-FR")} au{" "}
                    {new Date(t.valid_until).toLocaleDateString("fr-FR")})
                  </button>
                </li>
              ))}
          </ul>
        </div>

        {/* Next arrow */}
        <button
          className="btn btn-light btn-icon ms-1"
          onClick={() => {
            const idx = timetables.findIndex(
              (t) => t?.edt_id === selected?.edt_id,
            );
            if (idx < timetables.length - 1) handleSelect(timetables[idx + 1]);
          }}
          disabled={
            !selected ||
            timetables.findIndex((t) => t?.edt_id === selected?.edt_id) ===
              timetables.length - 1
          }
          title="Emploi du temps suivant"
        >
          <i className="ti ti-chevron-right" />
        </button>
      </div>
      <EDTApercuByTeacher
        show={showTeacherModal}
        metaData={{ id_versionEDT: selected?.versionedts[0]?.version_id }}
        onClose={() => setShowTeacherModal(false)}
      />
    </>
  );
};

export default EDTTooltipOption;
